// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Sizes, crops and names — the arithmetic that decides what is actually
 * rendered, kept out of the renderer so it can be tested without a GPU.
 */

export type AspectId = 'portrait' | 'square' | 'landscape' | 'source';

export interface OutputSize {
  readonly width: number;
  readonly height: number;
}

export const ASPECTS: readonly { id: AspectId; label: string; hint: string }[] = [
  { id: 'source', label: 'As shot', hint: "the photograph's own shape" },
  { id: 'portrait', label: 'Portrait', hint: '1080 × 1920 — stories, reels' },
  { id: 'square', label: 'Square', hint: '1080 × 1080 — feed posts' },
  { id: 'landscape', label: 'Landscape', hint: '1920 × 1080 — everywhere else' },
];

/**
 * H.264 and HEVC both require even dimensions; mediabunny throws otherwise.
 *
 * The floor of 2 is not decoration. Rounding alone sends anything under 0.5 to
 * zero, which is even, and a zero-width encode fails at the very end of a long
 * render — the worst possible place for it.
 */
export function evenise(n: number): number {
  const r = Math.round(n);
  const even = r % 2 === 0 ? r : r + 1;
  return Math.max(2, even);
}

/** The longest edge any exported clip will have. */
export const MAX_OUTPUT_EDGE = 1920;

export function outputSize(aspect: AspectId, sourceWidth: number, sourceHeight: number): OutputSize {
  switch (aspect) {
    case 'portrait':
      return { width: 1080, height: 1920 };
    case 'square':
      return { width: 1080, height: 1080 };
    case 'landscape':
      return { width: 1920, height: 1080 };
    case 'source': {
      const long = Math.max(sourceWidth, sourceHeight);
      const scale = long > MAX_OUTPUT_EDGE ? MAX_OUTPUT_EDGE / long : 1;
      return {
        width: evenise(sourceWidth * scale),
        height: evenise(sourceHeight * scale),
      };
    }
    default: {
      const exhaustive: never = aspect;
      throw new Error(`unknown aspect ${String(exhaustive)}`);
    }
  }
}

export interface CoverUv {
  readonly scaleU: number;
  readonly scaleV: number;
  readonly offsetU: number;
  readonly offsetV: number;
}

/**
 * Centre-crop the source so it fills the output frame without letterboxing.
 *
 * Returns the transform applied to the output's [0,1] coordinates to get source
 * texture coordinates: `uv_src = uv_out * scale + offset`. A scale below 1 on
 * an axis means that axis is being cropped.
 */
export function coverUv(
  sourceWidth: number,
  sourceHeight: number,
  outWidth: number,
  outHeight: number,
): CoverUv {
  const srcAspect = sourceWidth / sourceHeight;
  const outAspect = outWidth / outHeight;
  if (srcAspect > outAspect) {
    // Source is wider: crop its sides.
    const scaleU = outAspect / srcAspect;
    return { scaleU, scaleV: 1, offsetU: (1 - scaleU) / 2, offsetV: 0 };
  }
  const scaleV = srcAspect / outAspect;
  return { scaleU: 1, scaleV, offsetU: 0, offsetV: (1 - scaleV) / 2 };
}

/**
 * The size the photograph is handed to the depth model at.
 *
 * Two independent reasons for a cap, both measured. The DPT preprocessor keeps
 * the aspect ratio and pins whichever axis is closest to a scale of 1 at 518
 * rounded to a multiple of 14, so a wide panorama is fed to the network at
 * 2072 x 518 — four times the compute of a square — no matter how the source is
 * sized. And the pipeline's final bilinear upsample back to the ORIGINAL image
 * size always runs as a separate single-threaded WebAssembly session even when
 * the network itself ran on the GPU, so a 12 MP source means a single-threaded
 * resize producing a 48 MB float array.
 *
 * 1600 px on the long edge is far more than a 256 x 256 displaced grid can use
 * and keeps both costs bounded.
 */
export const MAX_INFERENCE_EDGE = 1600;

export function inferenceSize(width: number, height: number): OutputSize {
  const long = Math.max(width, height);
  if (long <= MAX_INFERENCE_EDGE) return { width: Math.round(width), height: Math.round(height) };
  const scale = MAX_INFERENCE_EDGE / long;
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/** The network's own training resolution, and the base for the size below. */
export const BASE_PROCESSOR_SIZE = 518;

/**
 * How large a square to tell the preprocessor to aim for.
 *
 * Shrinking the SOURCE does not bound the network's input, which is the trap
 * here. When the source is larger than the target on both axes, the DPT
 * processor picks whichever scale factor is closer to 1 — always the one for
 * the SHORTER side — and applies it to both, so the short side lands on the
 * target and the long side becomes `target x aspect`. The work is therefore
 * `target² x aspect` **whatever size the source was**: a 4:1 panorama costs four
 * times a square at any resolution.
 *
 * So the target itself has to come down for a long picture. This keeps the
 * network's input near a fixed pixel budget, rounded to the multiple of 14 the
 * graph requires — its own output shape is literally `14*floor(height/14)`.
 */
export function processorSize(width: number, height: number): number {
  const aspect = Math.max(width, height) / Math.max(1, Math.min(width, height));
  const budget = BASE_PROCESSOR_SIZE * BASE_PROCESSOR_SIZE * 1.6;
  const ideal = Math.sqrt(budget / aspect);
  const clamped = Math.min(BASE_PROCESSOR_SIZE, Math.max(196, ideal));
  return Math.max(196, Math.floor(clamped / 14) * 14);
}

/**
 * The largest source texture we will hand to WebGL.
 *
 * A flatbed scan at 600 dpi is 6,000 pixels on the long edge and a 1,200 dpi one
 * is twice that; `MAX_TEXTURE_SIZE` is commonly 8,192 and 4,096 on older
 * devices. Past the limit `texImage2D` sets INVALID_VALUE and leaves a BLACK
 * texture — it does not throw, so the failure arrives as a black clip rather
 * than as an error. The cap is read from the driver and then held well below it.
 */
export function textureCap(maxTextureSize: number): number {
  return Math.max(1024, Math.min(4096, maxTextureSize));
}

export const FPS = 30;

export function frameCount(seconds: number, fps = FPS): number {
  return Math.max(2, Math.round(seconds * fps));
}

/**
 * A bitrate ceiling for the clip.
 *
 * `new Quality({ bitrate })` is variable-bitrate, so this is a cap and not a
 * target: a 12 Mbps cap measured 2.6 Mbps of actual output on smooth gradient
 * content. Parallax from a still photograph is about as smooth as video gets,
 * so real files land far below this — which is why the interface never
 * estimates a file size from it.
 */
export function bitrateCeiling(width: number, height: number, fps = FPS): number {
  const bits = width * height * fps * 0.12;
  return Math.round(Math.min(24e6, Math.max(4e6, bits)));
}

/** Strip a photograph's extension and anything awkward, for artefact names. */
export function baseName(fileName: string): string {
  const withoutExt = fileName.replace(/\.[A-Za-z0-9]{1,5}$/, '');
  const cleaned = withoutExt
    .replace(/[^\p{L}\p{N}._-]+/gu, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '')
    .slice(0, 60);
  return cleaned || 'photo';
}

export interface ArtefactNames {
  readonly clip: string;
  readonly depth: string;
  readonly bokeh: string;
  readonly anaglyph: string;
}

export function artefactNames(fileName: string, container: 'mp4' | 'webm'): ArtefactNames {
  const stem = baseName(fileName);
  return {
    clip: `${stem}-parallax.${container}`,
    depth: `${stem}-depth-16bit.png`,
    bokeh: `${stem}-bokeh.jpg`,
    anaglyph: `${stem}-anaglyph.png`,
  };
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
  if (bytes >= 1e3) return `${Math.round(bytes / 1e3)} KB`;
  return `${bytes} B`;
}
